import { Injectable, inject } from '@angular/core';

import { FavouritesConfig } from '@frontend/sports/common/client-config-data-access';
import { LDFeatureFlags, LaunchDarklyFlagManagerService } from '@frontend/sports/launch-darkly/feature';
import { WidgetLayoutResponse, WidgetPage, WidgetType } from '@frontend/sports/types/components/widget';
import { Observable, combineLatest, map, of } from 'rxjs';

/**
 * This service is for defining any custom logic for experimenting with widgets. The generic widget layout/slot cannot be changed for widget-specific experiments. Here you can add your feature flag and create a private function with your custom experiment logic to filter or change the widgets accordingly.
 */
@Injectable()
export class WidgetExperimentService {
    private readonly launchDarklyFlagManagerService = inject(LaunchDarklyFlagManagerService);
    /**
     * Adding unique kill switch behavior. If the feature is not enabled do NOT wait for LD while rending the widgets.
     */
    private readonly favouritesConfig = inject(FavouritesConfig);

    private readonly FAVOURITES_GRID_EXPERIMENT_TiME_OUT = 2000;

    /**
     * Defines all active feature flags for the widgets and handles the mapping on the data according to the conditions.
     * @param widgetLayout
     * @returns
     */
    filterWidgetsForExperiments$(widgetLayout: WidgetLayoutResponse): Observable<WidgetLayoutResponse> {
        const isFavouritesGridWidgetConfigEnabled = this.favouritesConfig.isFavouritesGridWidgetEnabled;
        const favouritesGridExperiment$ = isFavouritesGridWidgetConfigEnabled
            ? this.launchDarklyFlagManagerService.getFeatureFlagValueWithCustomConditions$(LDFeatureFlags.SportsHomeScreenYourFavouritesWidget, {
                  allowedEmissions: 1,
                  timeoutInMS: this.FAVOURITES_GRID_EXPERIMENT_TiME_OUT,
                  defaultValue: false,
              })
            : of(false);

        return combineLatest([favouritesGridExperiment$]).pipe(
            map(([isFavouritesGridExperimentEnabled]) => {
                let widgetModel = widgetLayout;

                if (isFavouritesGridWidgetConfigEnabled) {
                    widgetModel = this.favouritesGridExperiment(widgetModel, isFavouritesGridExperimentEnabled);
                }

                return widgetModel;
            }),
        );
    }

    /**
     * This experiment only applies on home lobby, it hides the favourites widget if the favourites grid is enabled.
     * @param widgetLayout
     * @returns
     */
    private favouritesGridExperiment(widgetLayout: WidgetLayoutResponse, isFavouritesGridEnabled: boolean): WidgetLayoutResponse {
        if (widgetLayout.page !== WidgetPage.HomeLobby) {
            return widgetLayout;
        }

        const filteredWidgets = isFavouritesGridEnabled
            ? widgetLayout.widgets?.filter((widget) => widget.type !== WidgetType.Favourites)
            : widgetLayout.widgets?.filter((widget) => widget.type !== WidgetType.FavouritesGrid);

        return {
            ...widgetLayout,
            widgets: filteredWidgets,
        };
    }
}
